import * as Sentry from '@sentry/nextjs'
import { Severity } from '@sentry/nextjs'
import axios, { AxiosError, AxiosRequestHeaders, InternalAxiosRequestConfig } from 'axios'

import { forceUserLogout, getAccessToken, getRefreshToken, updateAccessToken } from '@/auth/token'
import { LOGIN_URL, REFRESH_TOKEN_URL } from '@/services/auth'
import { DATA_INSIGHTS_API } from '@/services/baseUrl'
import { isInShipperGuideIframe, requestShipperGuideToRefreshToken } from '@/utils/shipperGuide'

const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
})

/** Interceptor to include Access Token on requests */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()
    if (token && config.url?.includes(DATA_INSIGHTS_API)) {
      config = {
        ...config,
        headers: {
          ...config.headers,
          Authorization: 'Bearer ' + token,
        } as AxiosRequestHeaders,
      }
    }
    return config
  },
  requestError => {
    return Promise.reject(requestError)
  }
)

type AxiosRequestConfigWithRetried = InternalAxiosRequestConfig & {
  _retried?: boolean
  _retries?: number
}

const shouldTryRefreshToken = (
  requestConfig: AxiosRequestConfigWithRetried,
  responseError: AxiosError<XMLHttpRequest>
) => {
  return (
    // The request is not a login request or a refresh token request
    requestConfig.url !== LOGIN_URL &&
    requestConfig.url !== REFRESH_TOKEN_URL &&
    // The request has a response
    responseError.response &&
    // The response status is 401 (unauthorized)
    responseError.response.status === 401 &&
    // The request hasn't already been retried
    !requestConfig._retried
  )
}

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken()

  if (!refreshToken) throw new Error('No refresh token available')

  const response = await axiosInstance.post(REFRESH_TOKEN_URL, {
    refresh: refreshToken,
  })
  const { access: newAccessToken } = response.data
  updateAccessToken(newAccessToken)
}

type FailedRequestSentryEvent = {
  message: string
  extra: { requestUrl: string }
  tags?: { 'http.status_code': string }
  fingerprint: string[]
}

const UUID_PATH_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const NUMERIC_ID_PATH_SEGMENT = /^\d+$/

/**
 * Host + path only, with IDs collapsed. Query strings (rolling dates) and
 * shipper UUIDs must not appear in Sentry issue titles or they split into a
 * new issue on every synthetic run / customer.
 */
const toRequestRoute = (url: string | undefined): string => {
  if (!url) return 'unknown URL'

  try {
    const isAbsolute = /^https?:\/\//i.test(url)
    const parsed = new URL(url, 'http://localhost')
    const path = parsed.pathname
      .split('/')
      .filter(Boolean)
      .map(segment =>
        UUID_PATH_SEGMENT.test(segment) || NUMERIC_ID_PATH_SEGMENT.test(segment) ? ':id' : segment
      )
      .join('/')
    if (!path) return isAbsolute ? parsed.host : 'unknown URL'
    return isAbsolute ? `${parsed.host}/${path}` : path
  } catch {
    return url.split('?')[0] || 'unknown URL'
  }
}

/**
 * Server errors only. Aborts, timeouts, and 4xx are expected (navigation,
 * synthetics, missing resources) and would page Phoenix on first seen.
 */
const shouldReportFailedRequestToSentry = (error: AxiosError<XMLHttpRequest>) => {
  const httpStatus = error.response?.status
  return httpStatus != null && httpStatus >= 500
}

/**
 * Builds a stable Sentry event for a failed HTTP request.
 *
 * Status first, stripped route last, so Slack/Sentry truncation still shows
 * why it failed. Host + path (IDs collapsed) keeps endpoints distinguishable
 * without query-param cardinality. Full URL lives in extra.
 */
const buildFailedRequestSentryEvent = (
  url: string | undefined,
  error: AxiosError<XMLHttpRequest>
): FailedRequestSentryEvent => {
  const extra = { requestUrl: url ?? 'unknown URL' }
  const requestRoute = toRequestRoute(url)
  const httpStatus = error.response?.status as number

  return {
    message: `Request failed with status ${httpStatus}: ${requestRoute}`,
    extra,
    tags: { 'http.status_code': String(httpStatus) },
    fingerprint: ['http-client-failed-request', String(httpStatus), requestRoute],
  }
}

/** Response interceptor to catch expired tokens request and request a new token with the Refresh token */
axiosInstance.interceptors.response.use(
  res => {
    return res
  },
  async (responseError: AxiosError<XMLHttpRequest>) => {
    const requestConfig = responseError.config as AxiosRequestConfigWithRetried

    if (shouldTryRefreshToken(requestConfig, responseError)) {
      if (isInShipperGuideIframe()) {
        if (requestConfig._retries && requestConfig._retries > 10) {
          // prevents infinite loop if one of our endpoints is correctly returning 401
          Sentry.captureException(
            `Failed to refresh token from ShipperGuide when trying to access ${requestConfig.url}`
          )
          return Promise.reject(responseError)
        }
        // If the user is in the Shipper Guide iframe, request shipper guide to refresh the token then resets the page
        requestShipperGuideToRefreshToken()
        await new Promise(resolve => setTimeout(resolve, 1000)) // waiting a while before retry
        requestConfig._retries = requestConfig._retries ? requestConfig._retries + 1 : 1 // counting retries
        return axiosInstance(requestConfig)
      } else {
        requestConfig._retried = true // Mark the request as retried
        try {
          // get a new refresh token and retry the original request
          await refreshAccessToken()
          return axiosInstance(requestConfig)
        } catch (refreshRequestError) {
          // Refresh token no longer valid, so force the user to log in again
          forceUserLogout()
          return Promise.reject(refreshRequestError)
        }
      }
    }
    // If we got here, we couldn't retry the original request
    if (shouldReportFailedRequestToSentry(responseError)) {
      const sentryEvent = buildFailedRequestSentryEvent(requestConfig?.url, responseError)
      Sentry.captureMessage(sentryEvent.message, {
        level: Severity.Error,
        extra: sentryEvent.extra,
        tags: sentryEvent.tags,
        fingerprint: sentryEvent.fingerprint,
      })
    }
    return Promise.reject(responseError)
  }
)
const httpClient = axiosInstance

export { httpClient }
